import { useEffect, useState } from 'react'
import { Download, Share, X } from 'lucide-react'

/**
 * InstallPrompt (F5)
 *
 * Banner discreto para instalar o PWA:
 *  - Android/Chrome: captura `beforeinstallprompt` e dispara `prompt()`
 *    quando o usuario clica em "Instalar"
 *  - iOS Safari: como nao expoe API de instalacao, mostra instrucoes
 *    "Compartilhar -> Adicionar a Tela de Inicio"
 *
 * Aparece UMA vez por aparelho (flag em localStorage, com try/catch: em
 * aba privada ou com storage bloqueado o banner simplesmente não insiste) e
 * não volta após "Mais tarde", "Entendi" ou instalação. Decisão do David
 * (29/10/2026): o PWA é o app oficial do colaborador, então o convite é
 * imediato, não depois da 2ª visita.
 */

const DISMISSED_KEY = '4queijos.pwa.installDismissed'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    // Sem storage não dá pra garantir "uma vez só" — melhor não mostrar
    // do que mostrar em toda abertura.
    return true
  }
}
function markDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    /* ignora */
  }
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isStandalone() {
  if (typeof window === 'undefined') return false
  // iOS Safari
  const nav = window.navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true
  return window.matchMedia('(display-mode: standalone)').matches
}

function detectIos() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const isIDevice = /iPad|iPhone|iPod/.test(ua)
  // Excluir IE/Opera Mini
  const isOpera = /OPR|Opera/.test(ua)
  return isIDevice && !isOpera
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false)
  const [iosHelpOpen, setIosHelpOpen] = useState(false)
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isIos, setIsIos] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (isStandalone()) return // ja instalado
    if (readDismissed()) return

    setIsIos(detectIos())

    const handler = (event: Event) => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
      setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', handler)

    const installed = () => {
      setVisible(false)
      markDismissed()
    }
    window.addEventListener('appinstalled', installed)

    // No iOS, o evento nunca dispara — mostra o card apos visita 2 se for iOS
    if (detectIos()) {
      setVisible(true)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installed)
    }
  }, [])

  if (!visible) return null

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') {
        markDismissed()
      }
      setDeferredPrompt(null)
      setVisible(false)
    } else if (isIos) {
      setIosHelpOpen(true)
    }
  }

  const handleDismiss = () => {
    markDismissed()
    setVisible(false)
  }

  return (
    <>
      <div
        role="dialog"
        aria-label="Instalar aplicativo 4 Queijos"
        data-testid="install-banner"
        className="md:hidden fixed inset-x-3 z-40 rounded-2xl border border-border-color bg-surface px-4 py-3 shadow-lg"
        style={{
          bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">
              Instalar 4 Queijos
            </div>
            <p className="mt-0.5 text-xs leading-snug text-foreground-muted">
              {isIos
                ? 'Adicione o app à tela inicial para abrir em tela cheia, sem barra do navegador.'
                : 'Use como app: abre rápido, ícone na tela inicial, funciona offline básico.'}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={handleInstall}
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-brand px-3 text-xs font-semibold text-white hover:bg-brand-hover"
              >
                {isIos ? 'Como instalar' : 'Instalar'}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="inline-flex h-9 items-center rounded-full px-3 text-xs font-medium text-foreground-muted hover:bg-surface-alt"
              >
                Mais tarde
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar"
            className="-mr-1 -mt-1 grid h-7 w-7 place-items-center rounded-full text-foreground-muted hover:bg-surface-alt"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {iosHelpOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={(e) => e.target === e.currentTarget && setIosHelpOpen(false)}
        >
          <div className="w-full max-w-sm rounded-3xl border border-border-color bg-surface p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-foreground">
                Adicionar à Tela de Início
              </h3>
              <button
                type="button"
                onClick={() => setIosHelpOpen(false)}
                aria-label="Fechar"
                className="grid h-8 w-8 place-items-center rounded-full text-foreground-muted hover:bg-surface-alt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ol className="mt-4 space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-2">
                <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
                  1
                </span>
                <span className="leading-snug">
                  Toque no botão{' '}
                  <span className="inline-flex items-center gap-1 rounded-md border border-border-color bg-surface-alt px-1.5 py-0.5 align-middle">
                    <Share className="h-3.5 w-3.5" />
                    Compartilhar
                  </span>{' '}
                  na barra inferior do Safari.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
                  2
                </span>
                <span className="leading-snug">
                  Role a lista e escolha{' '}
                  <strong>"Adicionar à Tela de Início"</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
                  3
                </span>
                <span className="leading-snug">
                  Confirme em <strong>"Adicionar"</strong> no canto superior
                  direito.
                </span>
              </li>
            </ol>

            <button
              type="button"
              onClick={() => {
                markDismissed()
                setIosHelpOpen(false)
                setVisible(false)
              }}
              className="mt-5 w-full rounded-full bg-brand py-3 text-sm font-semibold text-white hover:bg-brand-hover"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default InstallPrompt
